import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Tag } from 'antd';
import moment from 'moment';
import callApi from '../../Api/config';
import { Typography } from 'antd';
import DefaultTableHeader from '../Entity/AbstractLanding/DefaultTableHeader';
import AbstractTabs, { TabActiosEventType, TabActiosProps } from '../Entity/AbstractTabs';
import { AGENT_LEDGER } from '../../constants/EntityName';
import { ContentItem, TabContent } from '../../utils/forms/FormInterface';
import AgentLedgerPaymentDetails from './AgentLedgerPaymentDetails';
import { toastError } from '../../service/Toast';

type ReviewStatus = 'pending' | 'accepted' | 'rejected' | 'partial';

const REVIEW_STATUS_META: Record<ReviewStatus, { color: string; label: string }> = {
  pending: { color: 'warning', label: 'Pending Review' },
  accepted: { color: 'success', label: 'Accepted' },
  rejected: { color: 'error', label: 'Rejected' },
  partial: { color: 'gold', label: 'Partial' },
};

const isStripePayment = (payment: any): boolean => {
  const method = String(payment?.provider || payment?.paymentMethod || '').toLowerCase();
  return method.includes('stripe') || Boolean(payment?.stripePaymentIntentId);
};

const isPaidStatus = (status: string): boolean => {
  const s = (status || '').toLowerCase();
  return (
    s.includes('paid') ||
    s === 'succeeded' ||
    s.includes('payment_done') ||
    s.includes('payment-done') ||
    s.includes('payment successful')
  );
};

/** Review status only applies to Zelle/Venmo. Stripe has no manual review. */
const getReviewStatus = (payment: any): ReviewStatus | null => {
  if (isStripePayment(payment)) {
    return isPaidStatus(payment?.status) ? 'accepted' : null;
  }
  if (payment?.reviewStatus === 'partial') return 'partial';
  if (
    payment?.reviewStatus === 'accepted' ||
    payment?.reviewStatus === 'rejected' ||
    payment?.reviewStatus === 'pending'
  ) {
    return payment.reviewStatus as ReviewStatus;
  }
  const status = (payment?.status || '').toLowerCase();
  if (isPaidStatus(status)) return 'accepted';
  if (status === 'partial' || status.includes('partial')) return 'partial';
  if (status.includes('payment_verification_pending')) return 'pending';
  if (status === 'failed' || status.includes('unsuccessful')) {
    return payment?.verifiedAt ? 'rejected' : 'pending';
  }
  return payment?.verifiedAt ? 'rejected' : 'pending';
};

const matchesPaymentStatusFilter = (payment: any, filterValue: string) => {
  const s = (payment?.status || '').toLowerCase();
  const filter = (filterValue || '').toLowerCase();
  const stripe = isStripePayment(payment);

  if (filter === 'payment_confirmation_pending') {
    // Confirmation pending is Zelle/Venmo only â€” never Stripe
    if (stripe) return false;
    return s.includes('payment_verification_pending') || s.includes('confirmation');
  }
  if (filter === 'paid') {
    return isPaidStatus(s);
  }
  if (filter === 'payment_pending') {
    if (stripe) {
      return (
        !isPaidStatus(s) &&
        (s === 'pending' || s.includes('payment_pending') || s.includes('payment pending'))
      );
    }
    return s === 'payment_pending' || s.includes('payment pending');
  }
  if (filter === 'failed') {
    return s.includes('failed') || s.includes('unsuccessful') || s.includes('rejected');
  }
  if (filter === 'partial') {
    if (stripe) return false;
    return s.includes('partial');
  }
  return s.includes(filter);
};

const PendingPaymentVerifications = () => {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSearch, setFilterSearch] = useState<Record<string, any>>({});
  const [activeKey, setActiveKey] = useState('0');
  const [content, setContent] = useState<TabContent>([]);

  const fetchPendingPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await callApi('/partner-portal/admin/payments/pending', 'GET');
      if (res?.status) {
        const rawData = res?.data?.data || res?.data;
        const items = Array.isArray(rawData) ? rawData : rawData?.results || rawData?.items || [];
        setPayments(items);
      } else {
        setPayments([]);
      }
    } catch (error) {
      console.error('Error fetching pending payments:', error);
      toastError('Failed to fetch agent ledger payments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingPayments();
  }, [fetchPendingPayments]);

  const onTabClick = (key: string) => {
    if (key === '0' || content.some(tab => String(tab.key) === key)) {
      setActiveKey(key);
    } else {
      setActiveKey('0');
    }
  };

  const updateFormVisibility = (
    action: TabActiosProps,
    targetKey?: string | TabActiosEventType
  ) => {
    if (action === 'remove') {
      const key = String(targetKey);
      setContent(prev => prev.filter(item => String(item.key) !== key));
      setActiveKey('0');
    }
  };

  const openPaymentTab = useCallback(
    (payment: any) => {
      if (!payment?.id) return;
      const key = String(payment.id);
      const existing = content.find(item => String(item.key) === key);
      if (existing) {
        setActiveKey(key);
        return;
      }

      const tabTitle = `Review · ${payment.agentId || payment.partnerName || String(payment.id).slice(0, 8)}`;

      const newTab: ContentItem = {
        key,
        tabTitle,
        content: <AgentLedgerPaymentDetails payment={payment} onUpdated={fetchPendingPayments} />,
      };
      setContent(prev => [...prev, newTab]);
      setActiveKey(key);
    },
    [content, fetchPendingPayments]
  );

  const filteredPayments = useMemo(() => {
    let rows = [...payments];
    const q = searchQuery.trim().toLowerCase();

    if (q) {
      rows = rows.filter(payment => {
        const haystack = [
          payment.agentId,
          payment.partnerId,
          payment.partnerName,
          payment.partnerEmail,
          payment.transactionId,
          payment.stripePaymentIntentId,
          payment.paymentId,
          payment.id,
          payment.countryName,
          payment.provider,
          payment.paymentMethod,
          payment.status,
          ...(payment.orderCodes || []),
          ...(payment.orderStatuses || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    if (filterSearch?.paymentStatus) {
      rows = rows.filter(payment =>
        matchesPaymentStatusFilter(payment, String(filterSearch.paymentStatus))
      );
    }

    if (filterSearch?.paymentMethod) {
      const method = String(filterSearch.paymentMethod).toLowerCase();
      rows = rows.filter(payment => {
        const value = `${payment.provider || ''} ${payment.paymentMethod || ''}`.toLowerCase();
        return value.includes(method);
      });
    }

    if (filterSearch?.reviewStatus) {
      rows = rows.filter(payment => {
        const review = getReviewStatus(payment);
        // Stripe unpaid has no review status
        if (!review) return false;
        return review === filterSearch.reviewStatus;
      });
    }

    if (filterSearch?.startDate && filterSearch?.endDate) {
      const start = moment(filterSearch.startDate).startOf('day');
      const end = moment(filterSearch.endDate).endOf('day');
      rows = rows.filter(payment => {
        if (!payment.createdAt) return false;
        return moment(payment.createdAt).isBetween(start, end, undefined, '[]');
      });
    }

    return rows;
  }, [payments, searchQuery, filterSearch]);

  const formatMoney = (amount: any) => `$${Number(amount || 0).toFixed(2)}`;

  const formatPaymentStatus = (payment: any) => {
    const s = (payment?.status || '').toLowerCase();
    const stripe = isStripePayment(payment);

    if (isPaidStatus(s)) {
      return { color: 'success', label: 'Paid' };
    }

    // Stripe: only Paid / Unpaid â€” never "Payment Confirmation Pending"
    if (stripe) {
      return { color: 'processing', label: 'Unpaid' };
    }

    if (s.includes('payment_verification_pending') || s.includes('confirmation')) {
      return { color: 'warning', label: 'Payment Confirmation Pending' };
    }
    if (s.includes('failed') || s.includes('unsuccessful') || s.includes('rejected')) {
      return { color: 'error', label: 'Payment Unsuccessful' };
    }
    if (s.includes('partial')) {
      return { color: 'warning', label: 'Partial Payment Received' };
    }
    if (s === 'pending' || s.includes('payment_pending') || s.includes('payment pending')) {
      return { color: 'processing', label: 'Payment Pending' };
    }
    return { color: 'processing', label: payment?.status || 'Unpaid' };
  };

  const columns = [
    {
      title: 'Agent ID',
      dataIndex: 'agentId',
      key: 'agentId',
      render: (value: string, record: any) => (
        <span
          style={{ textDecoration: 'underline', cursor: 'pointer' }}
          onClick={e => {
            e.stopPropagation();
            openPaymentTab(record);
          }}
        >
          {value || record.partnerId || '—'}
        </span>
      ),
    },
    {
      title: 'Agent Name',
      dataIndex: 'partnerName',
      key: 'partnerName',
      render: (name: string) => name || '—',
    },
    {
      title: 'Transaction ID',
      key: 'transactionId',
      render: (_: any, record: any) => {
        const paymentId =
          record.stripePaymentIntentId ||
          record.transactionId ||
          record.paymentId ||
          record.id ||
          '';
        if (!paymentId) return '—';
        return (
          <Typography.Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {paymentId}
          </Typography.Text>
        );
      },
    },
    {
      title: 'Orders',
      key: 'orders',
      render: (record: any) => {
        const codes = record.orderCodes || [];
        if (codes.length) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {codes.map((code: string) => (
                <span key={code} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {code}
                </span>
              ))}
            </div>
          );
        }
        return record.ordersCount || 0;
      },
    },
    {
      title: 'Country Name',
      dataIndex: 'countryName',
      key: 'countryName',
      render: (value: string) => value || '—',
    },
    {
      title: 'Email ID',
      dataIndex: 'partnerEmail',
      key: 'partnerEmail',
      render: (email: string) => email || '—',
    },
    {
      title: 'Paid Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => <span style={{ fontWeight: 600 }}>{formatMoney(amount)}</span>,
    },
    {
      title: 'Net Received',
      dataIndex: 'netReceived',
      key: 'netReceived',
      render: (amount: number, record: any) => {
        const reviewStatus = getReviewStatus(record);
        const value =
          amount ||
          (reviewStatus === 'accepted' || isPaidStatus(record?.status)
            ? Number(record.amount || 0)
            : 0);
        return formatMoney(value);
      },
    },
    {
      title: 'Partial Received',
      dataIndex: 'partialReceived',
      key: 'partialReceived',
      render: (amount: number, record: any) =>
        isStripePayment(record) ? '—' : formatMoney(amount),
    },
    {
      title: 'Payment Method',
      key: 'paymentMethod',
      render: (record: any) => (
        <span style={{ textTransform: 'capitalize' }}>
          {record.provider || record.paymentMethod || '—'}
        </span>
      ),
    },
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      render: (record: any) => {
        const meta = formatPaymentStatus(record);
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Payment Date',
      dataIndex: 'createdAt',
      key: 'paymentDate',
      render: (date: string) => (date ? new Date(date).toLocaleString() : '—'),
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: any) => {
        // Stripe has no CMS review workflow
        if (isStripePayment(record)) {
          return isPaidStatus(record?.status) ? (
            <Tag color="success">Auto Settled</Tag>
          ) : (
            <span>—</span>
          );
        }
        const reviewStatus = getReviewStatus(record);
        if (!reviewStatus) return <span>—</span>;
        const meta = REVIEW_STATUS_META[reviewStatus] || REVIEW_STATUS_META.pending;
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
  ];

  const listView = (
    <div>
      <DefaultTableHeader
        headerName="Agent Ledger"
        totalCount={filteredPayments.length}
        countText="records found"
        setSearchQuery={setSearchQuery}
        entityName={AGENT_LEDGER}
        addName="Agent"
        showFilter={true}
        filterQuery={setFilterSearch}
        disableAdd={true}
        showDownload={false}
      />
      <Table
        dataSource={filteredPayments}
        columns={columns}
        rowKey="id"
        loading={loading}
        bordered
        pagination={{ pageSize: 10, showTotal: total => `Total ${total} records` }}
        scroll={{ x: 'max-content' }}
        onRow={record => ({
          onClick: () => openPaymentTab(record),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );

  return (
    <AbstractTabs
      activeKey={activeKey}
      onTabClick={onTabClick}
      updateFormVisibility={updateFormVisibility}
      content={content}
      entityName="Agent Ledger"
    >
      {listView}
    </AbstractTabs>
  );
};

export default PendingPaymentVerifications;
